import { useState, useEffect } from "react";
import {
	LOAN_PURPOSE_PERSONAL,
	LOAN_PURPOSE_BUSINESS,
	EMP_STAT_SELF_EMPLOYED,
	LOAN_PURPOSE_NOVATED_LEASE,
	LOAN_TYPE_BOAT,
	LOAN_PURPOSE_DEBT_CONSOLIDATION,
	LOAN_PURPOSE_OTHER,
} from "@/lib/constants";
import { CarIcon, IslandIcon, SuitcaseIcon } from "@phosphor-icons/react";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui";

type LoanDetailsProps = {
	navRef?: boolean;
};

export const DefaultLoanDetails = (navRef: LoanDetailsProps) => {
	const { formValues, setFormValue } = useFormDataStore();
	const {
		selectedIndex,
		enabledBusinessFields,
		setEnabledBusinessFields,
		enabledFields,
		setFormError,
		setValidateFormDetails,
		formContainer,
	} = useFormStateStore();

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	const { loanType, loanPurpose, assetType, purchaseStage, assetAge, purchaseFrom } = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		loanPurpose: false,
		assetType: false,
		purchaseStage: false,
		assetAge: false,
		purchaseFrom: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			loanPurpose: enabledFields.loanPurpose?.length == 0 ? false : !loanPurpose,
			assetType: enabledFields.assetType?.length == 0 ? false : !assetType,
			purchaseStage: enabledFields.purchaseStage?.length == 0 ? false : !purchaseStage,
			assetAge: enabledFields.assetAge?.length == 0 ? false : !assetAge,
			purchaseFrom: enabledFields.purchaseFrom?.length == 0 ? false : !purchaseFrom,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);

		return !hasError; // Returns true if validation passes
	};

	// 86cyywnev - Set Loan Purpose automatically if it has only one option
	useEffect(() => {
		if (enabledFields.loanPurpose?.length === 1) {
			setFormValue("loanPurpose", enabledFields.loanPurpose[0]?.value);
			enabledFields.loanPurpose[0]?.value === LOAN_PURPOSE_BUSINESS
				? setFormValue("employmentStatus", EMP_STAT_SELF_EMPLOYED)
				: enabledFields.loanPurpose[0]?.value === LOAN_PURPOSE_PERSONAL
				? null //Don't initialise employmentStatus if Personal loan
				: setFormValue("employmentStatus", "");
		}
	}, []);

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [loanPurpose, assetType, purchaseStage, assetAge, purchaseFrom]);

	// Reset formValues where it's not enabled.
	useEffect(() => {
		const fieldsToReset: (keyof typeof formValues)[] = [
			"loanPurpose",
			"assetType",
			"purchaseStage",
			"assetAge",
			"purchaseFrom",
		];

		fieldsToReset.forEach((field) => {
			if (enabledFields[field]?.length === 0) {
				setFormValue(field, ""); // Reset value when no options are available
				setErrors((prevErrors) => ({
					...prevErrors,
					field: false,
				}));
			}
		});
	}, [enabledFields, setFormValue, setErrors]);

	return (
		<div className="jF-space-y-4 jF-m-2 jF-pb-4">
			{/* Loan Purpose */}
			{enabledFields.loanPurpose?.length > 1 && ( // Empty array means field is not supported.
				<RadioGroupField
					id="loanPurpose"
					label="Loan Purpose"
					value={loanPurpose || ""}
					options={enabledFields.loanPurpose.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_PURPOSE_PERSONAL ? (
								<IslandIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_BUSINESS ? (
								<SuitcaseIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_NOVATED_LEASE ? (
								<CarIcon size={24} weight="duotone" />
							) : null,
					}))}
					onChange={(value) => {
						setFormValue("loanPurpose", value);

						if (
							value === LOAN_PURPOSE_PERSONAL &&
							formValues.assetType === "Commercial Boat"
						) {
							setFormValue("assetType", "");
						}

						setFormValue(
							"employmentStatus",
							[
								LOAN_PURPOSE_PERSONAL,
								LOAN_PURPOSE_NOVATED_LEASE,
								LOAN_PURPOSE_DEBT_CONSOLIDATION, // Personal Loan
								LOAN_PURPOSE_OTHER, // Personal Loan
								LOAN_PURPOSE_BUSINESS,
							].includes(value)
								? ""
								: EMP_STAT_SELF_EMPLOYED
						);

						if (value === LOAN_PURPOSE_PERSONAL) {
							setFormValue("abnStatus", "");
							setFormValue("gstStatus", "");
							setFormValue("assetType", "");
						}
						setErrors((prevErrors) => ({
							...prevErrors,
							loanPurpose: !value, // Clear error when a selection is made
						}));
					}}
					required={true}
					error={errors.loanPurpose ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={3}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			{/* Asset Type - For Boat and Equipment Loans only*/}
			{enabledFields.assetType?.length != 0 && ( // Empty array means field is not supported.
				<RadioGroupField
					id="assetType"
					label="Asset Type"
					value={assetType || ""}
					options={enabledFields.assetType
						.filter((item) =>
							loanType === LOAN_TYPE_BOAT && loanPurpose === LOAN_PURPOSE_PERSONAL
								? item.value !== "Commercial Boat"
								: true
						)
						.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
					onChange={(value) => {
						setFormValue("assetType", value);
						setErrors((prevErrors) => ({
							...prevErrors,
							assetType: !value, // Clear error when a selection is made
						}));
					}}
					required={true}
					error={errors.assetType ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={formValues.loanType === LOAN_TYPE_BOAT ? 3 : 4}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			{/* Purchase Stage */}
			{enabledFields.purchaseStage?.length != 0 && (
				<RadioGroupField
					id="purchaseStage"
					label="Do you have one in mind?"
					value={purchaseStage || ""}
					options={enabledFields.purchaseStage.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon: null,
					}))}
					onChange={(value) => {
						setFormValue("purchaseStage", value);
						setErrors((prevErrors) => ({
							...prevErrors,
							purchaseStage: !value, // Clear error when a selection is made
						}));
					}}
					required={true}
					error={errors.purchaseStage ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={4}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			{/* Asset Age */}
			{enabledFields.assetAge?.length != 0 && (
				<RadioGroupField
					id="assetAge"
					label="What type of asset are you looking to purchase?"
					value={assetAge || ""}
					options={enabledFields.assetAge.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon: null,
					}))}
					onChange={(value) => {
						setFormValue("assetAge", value);
						setErrors((prevErrors) => ({
							...prevErrors,
							assetAge: !value, // Clear error when a selection is made
						}));
					}}
					required={true}
					error={errors.assetAge ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={4}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			{/* Purchase From */}
			{enabledFields.purchaseFrom?.length != 0 && (
				<RadioGroupField
					id="purchaseFrom"
					label="Where are you likely to purchase it from?"
					value={purchaseFrom || ""}
					options={enabledFields.purchaseFrom.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon: null,
					}))}
					onChange={(value) => {
						setFormValue("purchaseFrom", value);
						setErrors((prevErrors) => ({
							...prevErrors,
							purchaseFrom: !value, // Clear error when a selection is made
						}));
					}}
					required={true}
					error={errors.purchaseFrom ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={4}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}
		</div>
	);
};
